import { supabase } from './supabase';
import { supabaseUrl, supabaseKey } from '../config';

// -----------------------------------------------
// Payment constants — single source of truth
// -----------------------------------------------
export type PaymentGateway = 'stripe' | 'woovi';
export type PaymentFormat  = 'credit_card' | 'debit_card' | 'pix';

export type VenueWaitlistFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  venue: string;
  venueType: string[];
};

/**
 * Invoke Edge Function
 */
export async function invokeFunction<T = any>(
  functionName: string,
  body?: any,
  options?: {
    headers?: Record<string, string>;
  }
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(functionName, {
    body,
    headers: options?.headers || {},
  });

  if (error) {
    console.error(`Edge Function Error [${functionName}]:`, error);

    // supabase-js only surfaces a generic "non-2xx status" message on
    // FunctionsHttpError — the function's own { error, code } JSON body
    // lives on error.context (the raw Response) and has to be unpacked
    // manually, or callers can never branch on a specific error code.
    const context = (error as any).context;
    if (context && typeof context.json === 'function') {
      const body = await context.json().catch(() => null);
      if (body?.error) {
        const enrichedError = new Error(body.error);
        (enrichedError as any).code = body.code;
        (enrichedError as any).status = context.status;
        if (body.details !== undefined) (enrichedError as any).details = body.details;
        if (body.captured !== undefined) (enrichedError as any).captured = body.captured;
        throw enrichedError;
      }
    }

    throw error;
  }

  return data as T;
}

// -----------------------------------------------
// Payment routing — format → gateway
// -----------------------------------------------
const PAYMENT_GATEWAY_MAP: Record<PaymentFormat, PaymentGateway> = {
  credit_card: 'stripe',
  debit_card:  'stripe',
  pix:         'woovi',
};

export async function createCharge(
  format: PaymentFormat,
  params: { amount: number; orderId?: number; tabId?: number; correlationID?: string }
) {
  const gateway = PAYMENT_GATEWAY_MAP[format];

  if (gateway === 'woovi') {
    return invokeFunction('woovi-payment/create-charge', params);
  }

  return invokeFunction('stripe-payment/create-payment-intent', params);
}

/**
 * Stripe payment intent
 */
export async function createPaymentIntent(params: {
  amount: number;
  currency?: string;
  orderId?: number;
  tabId?: number;
  metadata?: Record<string, string>;
}) {
  return invokeFunction('stripe-payment', {
    action: 'create-payment-intent',
    ...params,
  });
}

/**
 * Confirm payment
 */
export async function confirmPayment(paymentIntentId: string, paymentMethodId: string) {
  return invokeFunction('stripe-payment/confirm-payment', {
    paymentIntentId,
    paymentMethodId,
  });
}

/**
 * Capture payment (customer-owned PI — tab close)
 */
export async function capturePayment(paymentIntentId: string) {
  return invokeFunction('stripe-payment/capture-payment', {
    paymentIntentId,
  });
}

/**
 * Capture authorized Stripe PIs for an order at delivery (venue staff).
 */
export async function captureOrderPayment(orderId: number) {
  const result = await invokeFunction<{
    captured?: Array<{ paymentIntentId: string; status: string; amountCents?: number }>;
    skipped?: boolean;
    reason?: string;
    error?: string;
    details?: Array<{ paymentIntentId?: string; splitId?: number; error: string }>;
    orderTotalCents?: number;
    capturedCents?: number;
  }>('stripe-payment/capture-order-payment', {
    orderId,
  });

  if (result?.error) {
    const err = new Error(result.error) as Error & { details?: unknown };
    err.details = result.details;
    throw err;
  }

  return result;
}

export function formatCaptureFailure(error: unknown): string {
  const err = error as { message?: string; details?: Array<{ error?: string }> };
  const base = err?.message || String(error);
  const details = Array.isArray(err?.details)
    ? err.details.map((d) => d?.error).filter(Boolean).join('; ')
    : '';
  return details ? `${base}: ${details}` : base;
}

/**
 * Full Stripe refund for a paid order or tab (venue admin/manager).
 */
export async function createStripeRefund(params: {
  orderId?: number;
  tabId?: number;
  venueId?: string;
  paymentIntentId?: string;
  chargeId?: string;
  amount?: number;
  reason?: 'duplicate' | 'fraudulent' | 'requested_by_customer';
}) {
  const result = await invokeFunction<{
    refundId?: string;
    status?: string;
    amount?: number;
    currency?: string;
    error?: string;
    code?: string;
  }>('stripe-payment/create-refund', params);

  if (result?.error) {
    const err = new Error(result.error);
    (err as any).code = result.code;
    throw err;
  }

  return result;
}

/**
 * Full Woovi PIX refund for a paid order or tab (venue admin/manager).
 */
export async function createWooviRefund(params: {
  orderId?: number;
  tabId?: number;
  venueId?: string;
  correlationID?: string;
  value?: number;
  comment?: string;
}) {
  const result = await invokeFunction<{
    refundCorrelationID?: string;
    status?: string;
    value?: number;
    correlationID?: string;
    error?: string;
    code?: string;
  }>('woovi-payment/create-refund', params);

  if (result?.error) {
    const err = new Error(result.error);
    (err as any).code = result.code;
    throw err;
  }

  return result;
}

/**
 * Send email
 */
export async function sendEmail(params: {
  to: string;
  subject: string;
  template: string;
  data?: Record<string, any>;
}) {
  return invokeFunction('send-email', params);
}

/**
 * Send push notification
 */
export async function sendPushNotification(params: {
  userId: string;
  title: string;
  body: string;
  data?: Record<string, any>;
}) {
  return invokeFunction('push-notification', params);
}

/**
 * Validate a signed QR code JWT (venue tab entry, etc.)
 */
export async function validateSecureQrCode(params: {
  token: string;
  venue_id: string;
  prop?: string;
}) {
  return invokeFunction<{ valid: boolean }>('generate-secure-qrcode', {
    action: 'validate',
    token: params.token,
    venue_id: params.venue_id,
    prop: params.prop ?? 'venue_id',
  });
}

/**
 * Send WhatsApp message with fastpass tab link
 */
export async function sendWhatsappTabLink(params: {
  user_id: string;
  tab_id: string;
}) {
  return invokeFunction('send-whatsapp', params);
}

/**
 * Notify customer (push + e-mail) when their tab is opened
 */
export async function notifyTabOpened(params: {
  user_id: string;
  tab_id: string;
}) {
  return invokeFunction('notify-tab-opened', params);
}

/**
 * Sync venue waitlist sign-up to HubSpot (public, no auth session required)
 */
export async function syncVenueUserWaitlist(formData: VenueWaitlistFormData) {
  const response = await fetch(`${supabaseUrl}/functions/v1/hubspot-sync/sync-venue-user`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${supabaseKey}`,
      apikey: supabaseKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ formData }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || data.error || `Request failed (${response.status})`);
  }

  return data as { message: string };
}

